import _ from 'lodash';
import { fetchRetry } from './utils';
import { PUBLIC_VOICE_ALIGN_WEBSITE } from '$env/static/public';
import { resolveLocalePlugin } from './utils.svelte';
import { logger } from '@sentry/sveltekit';

export type Waveform = {
	version: number;
	channels: number;
	sample_rate: number;
	samples_per_pixel: number;
	bits: number;
	length: number;
	data: number[];
};

export enum TrackType {
	Reference,
	Dub,
	Available,
	Unavailable
}

export enum PluginMode {
	Undefined,
	Ara,
	Capture
}

export enum PluginFormat {
	Undefined,
	Vst3,
	Au,
	Standalone,
	Ara
}

export type TrackData = {
	plugin_mode: PluginMode;
	type: TrackType;
	is_processing: boolean;
	processing_progress: number | undefined;
	is_bypassed: boolean;
	url: string;
	name: string;
	tag: string | undefined;
	order_index: number;
	region_sequence_id: number;
	waveform?: Waveform;
	waveform_rendered?: Waveform;
	id: number;
	can_save_to_file: boolean;
	can_load_from_file: boolean;
};

export function createTrackData(options: Partial<TrackData>) {
	const defaults = {
		plugin_mode: PluginMode.Ara,
		type: TrackType.Unavailable,
		is_processing: false,
		processing_progress: 0,
		is_bypassed: false,
		url: '/sounds/simulations_after.mp3',
		name: '-',
		order_index: 1,
		id: _.random(0, 100000000, false)
	} as TrackData;

	return { ...defaults, ...options };
}

export type TrackDataRef = {
	id: number;
};

export function createTrackDataRef(group: TrackData) {
	return { id: group.id } as TrackDataRef;
}

export type ProcessingGroup = {
	tracks: TrackData[];
	show_warning_no_reference_tracks: boolean;
	is_active: boolean;
	id: number;

	newly_created?: boolean;
};

export type ProcessingGroupRef = {
	id: number;
};

export function createProcessingGroupRef(group: ProcessingGroup) {
	return { id: group.id } as ProcessingGroupRef;
}

export function resolveRef(exchangeData: ExchangeData, ref: ProcessingGroupRef | undefined) {
	if (!ref) {
		return undefined;
	}
	return _.first(
		exchangeData.processing_groups?.filter((processing_group) => processing_group.id == ref.id)
	);
}

export type ExchangeData = {
	plugin_mode?: PluginMode;
	display_ara_chip?: boolean;
	display_capture_button?: boolean;
	is_capture_mode_active?: boolean;
	display_clear_button?: boolean;
	display_processing_groups?: boolean;
	processing_groups?: ProcessingGroup[];
	visible_processing_group?: ProcessingGroupRef;
	show_error_ara_required?: boolean;
	version?: string;
	activated?: boolean;
	processing_activation?: boolean;
	plugin_version?: string;
	plugin_version_tag?: string;
	plugin_format?: PluginFormat;
	is_production?: boolean;
};

export enum TimestretchAlgorithm {
	Signalsmith,
	Rubberband
}

export type PluginSettings = {
	time_stretch_algo: TimestretchAlgorithm;
};

export type SaveFileResult = {
	filename: string;
	success: boolean;
};

export type LoadFileResult = {
	filename: string;
	success: boolean;
};

export type UserIdentification = {
	id: string;
	username: string;
	email: string;
};

export type AnalyticsOptions = {
	consent: boolean;
	user?: UserIdentification;
};

export function createExchangeData(options: Partial<ExchangeData> = {}) {
	const defaults = {
		plugin_mode: PluginMode.Undefined,
		display_ara_chip: false,
		display_capture_button: false,
		is_capture_mode_active: false,
		display_clear_button: false,
		display_processing_groups: false,
		show_error_ara_required: false,
		activated: false,
		processing_activation: true
	} as ExchangeData;
	return { ...defaults, ...options };
}

export function createInitialExchangeData() {
	return createExchangeData({ activated: true });
}

export function createSampleExchangeData() {
	const tracks = [] as TrackData[];
	tracks.push(createTrackData({ order_index: 0, type: TrackType.Reference, name: 'Track 53' }));
	tracks.push(
		createTrackData({
			order_index: 1,
			type: TrackType.Dub,
			name: 'Track 54',
			is_processing: true,
			processing_progress: 25
		})
	);
	tracks.push(createTrackData({ order_index: 2, type: TrackType.Available, name: 'Track 55' }));
	tracks.push(createTrackData({ order_index: 3, type: TrackType.Unavailable, name: 'Track 56' }));

	const processing_groups = [] as ProcessingGroup[];

	const processing_group = {
		tracks,
		id: 420,
		show_warning_no_reference_tracks: false
	} as ProcessingGroup;
	processing_groups.push(processing_group);

	const visible_processing_group = { id: processing_group.id } as ProcessingGroupRef;
	const exchange_data = createExchangeData({
		processing_groups,
		visible_processing_group,
		activated: true,
		plugin_version: '0.0.0',
		plugin_version_tag: 'v0.0.0-beta.1',
		plugin_format: PluginFormat.Vst3
	});

	return exchange_data;
}

export class Controller {
	static async endpoint() {
		const juceFrontendFramework = await import('juce-framework-frontend-mirror');
		const getNativeFunction = juceFrontendFramework.getNativeFunction;
		const endpoint = getNativeFunction('get_server_endpoint');

		return await endpoint();
	}

	static runningWithJuce() {
		return (window?.__JUCE__?.initialisationData?.__juce__platform?.length ?? 0) > 0;
	}

	private static async get<T>(path: string): Promise<T> {
		try {
			const res = await fetchRetry(`${await this.endpoint()}${path}`);

			if (!res.ok) {
				const text = await res.text();
				throw new Error(`Request failed (${res.status}): ${text}`);
			}

			return (await res.json()) as T;
		} catch (error) {
			logger.error(`GET ${path} failed:`, { error });
			throw error;
		}
	}

	private static async post<T>(path: string, body?: unknown): Promise<T> {
		try {
			const res = await fetchRetry.json(`${await this.endpoint()}${path}`, body, {
				method: 'POST'
			});

			if (!res.ok) {
				const text = await res.text();
				throw new Error(`Request failed (${res.status}): ${text}`);
			}

			return (await res.json()) as T;
		} catch (error) {
			logger.error(`POST ${path} failed:`, { error });
			throw error;
		}
	}

	static get_exchange_data = (): Promise<ExchangeData> => {
		if (!this.runningWithJuce()) {
			return Promise.resolve(createSampleExchangeData());
		}
		return this.get<ExchangeData>('/exchange_data');
	};

	static get_plugin_settings = (): Promise<PluginSettings> => {
		if (!this.runningWithJuce()) {
			return Promise.resolve({ time_stretch_algo: TimestretchAlgorithm.Signalsmith });
		}
		return this.get<PluginSettings>('/plugin_settings');
	};

	static get_user_properties = async (key: string): Promise<string | null> => {
		if (!this.runningWithJuce()) {
			return localStorage.getItem(key);
		}
		return (await this.get<{ value?: string }>(`/user_properties/${key}`)).value ?? null;
	};

	static set_user_properties = async (key: string, value: string): Promise<void> => {
		if (!this.runningWithJuce()) {
			return localStorage.setItem(key, value);
		}
		return this.post<void>(`/user_properties/${key}`, { value });
	};

	static set_visible_processing_group = (ref: ProcessingGroupRef): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/set_visible_processing_group', { ref });
	};

	static add_new_processing_group = (): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/add_new_processing_group');
	};

	static remove_processing_group = (ref: ProcessingGroupRef): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/remove_processing_group', { ref });
	};

	static set_track_as_reference = (ref: TrackDataRef): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/set_track_as_reference', { ref });
	};

	static set_track_as_dub = (ref: TrackDataRef): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/set_track_as_dub', { ref });
	};

	static set_track_as_available = (ref: TrackDataRef): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/set_track_as_available', { ref });
	};

	static bypass_track = (ref: TrackDataRef): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/bypass_track', { ref });
	};

	static unbypass_track = (ref: TrackDataRef): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/unbypass_track', { ref });
	};

	static initiate_sign_in_with_google = async (tunnel_id: string): Promise<boolean> => {
		if (!this.runningWithJuce()) {
			window
				.open(
					`${PUBLIC_VOICE_ALIGN_WEBSITE}${await resolveLocalePlugin(
						'/(webapp)/[[locale=locale]]/plugin/login'
					)}?token_exchange_param=${tunnel_id}&method=jwt`
				)
				?.focus();
			return true;
		}

		return this.post<boolean>('/initiate_sign_in_with_google', { tunnel_id });
	};

	static open_url = (url: string): Promise<boolean> => {
		if (!this.runningWithJuce()) {
			window.open(url)?.focus();
			return Promise.resolve(true);
		}
		return this.post<boolean>('/open_url', { url });
	};

	static analytics = (options: AnalyticsOptions): Promise<void> => {
		return this.post<void>('/analytics', { options });
	};

	static send_crash_logs = (): Promise<string> => {
		return this.post<string>('/send_crash_logs');
	};

	static sign_in = (id_token: string, firebase_uid: string): Promise<void> => {
		if (!this.runningWithJuce()) {
			return Promise.resolve();
		}
		return this.post<void>('/sign_in', { id_token, firebase_uid });
	};

	static sign_out = (): Promise<void> => {
		if (!this.runningWithJuce()) {
			return Promise.resolve();
		}
		return this.post<void>('/sign_out');
	};

	static capture_mode_enable_capture_mode = (): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/capture_mode_enable_capture_mode');
	};

	static capture_mode_disable_capture_mode = (): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/capture_mode_disable_capture_mode');
	};

	static capture_mode_clear_audio = (): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/capture_mode_clear_audio');
	};

	static capture_mode_bypass_track = (ref: TrackDataRef): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/capture_mode_bypass_track', { ref });
	};

	static capture_mode_unbypass_track = (ref: TrackDataRef): Promise<ExchangeData> => {
		return this.post<ExchangeData>('/capture_mode_unbypass_track', { ref });
	};

	static save_file = async (ref: TrackDataRef): Promise<SaveFileResult> => {
		const result = this.post<SaveFileResult>('/save_file', { ref });
		if (!(await result).success) {
			throw new Error('Saving file failed');
		}
		return result;
	};

	static load_file = async (ref: TrackDataRef): Promise<LoadFileResult> => {
		const result = this.post<LoadFileResult>('/load_file', { ref });
		if (!(await result).success) {
			throw new Error('Loading file failed');
		}
		return result;
	};

	static plugin_settings_set_timestretch_algo = (
		value: TimestretchAlgorithm
	): Promise<PluginSettings> => {
		return this.post<PluginSettings>('/plugin_settings/timestretch_algo', { value });
	};
}
